/**
 * Row actions renderer for the Inspection Series listing.
 *
 * Recreates the three row-level actions from the legacy Kendo actionsTemplate:
 *   - View  (showLink, always shown when present)
 *   - Cancel (editStatusLink, hidden when isCancelled=true)
 *   - Update Expiration Date (hidden when isCancelled=true or the series has no building)
 *
 * Uses Rails UJS `data-remote="true"` for Cancel, preserving the existing modal/remote
 * flow. Update Expiration Date calls back to the listing, which opens the React modal
 * over the grid — no navigation.
 *
 * Quimby Button + Popup pattern (title + action section, no search): popup is
 * controlled via React state (show=false by default) — no menu is open on mount.
 * ClickAwayListener inside Popup handles outside-click dismissal via onClose.
 *
 * Action rows use `a.qmb-ui-button` (Popup ActionList convention) so they match
 * Quimby Button styling without nesting interactive elements, while keeping
 * native `href` / `target` / `data-remote` behavior.
 */
import React, { useRef, useState } from 'react';
import { translate } from '@utils/i18n';
import { Popup } from '@ui/molecules/Popup';
import { Button } from '@ui/atoms/Button';
const NS = 'inspection_series.datatable';
export function SeriesActionsRenderer({ item, onEditExpiration }) {
    const [show, setShow] = useState(false);
    const anchorRef = useRef(null);
    const hasActions = item.showLink || !item.isCancelled;
    if (!hasActions)
        return React.createElement("span", null);
    const closePopup = () => setShow(false);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { ref: anchorRef, variant: "default", "aria-label": translate('table.buttons.actions', { namespace: NS, defaultValue: 'Row actions' }), onClick: () => setShow((prev) => !prev) },
            React.createElement("i", { className: "fa-regular fa-ellipsis" })),
        React.createElement(Popup, { anchor: anchorRef.current, show: show, onClose: closePopup, position: "bottom-right" },
            React.createElement(Popup.Section, null,
                React.createElement(Popup.Title, null, translate('table.popup_title', { namespace: NS, defaultValue: 'Actions' })),
                React.createElement(Popup.ActionList, { padded: true },
                    item.showLink && (React.createElement("a", { href: item.showLink, title: translate('table.buttons.view_detail', { namespace: NS }), className: "qmb-ui-button", target: "blank", rel: "noopener noreferrer", onClick: closePopup },
                        React.createElement("i", { className: "fa-light fa-eye" }),
                        ` ${translate('table.buttons.view_detail', { namespace: NS })}`)),
                    !item.isCancelled && item.editStatusLink && (React.createElement("a", { href: item.editStatusLink, title: translate('table.buttons.cancel', { namespace: NS }), className: "qmb-ui-button", "data-remote": "true", onClick: closePopup },
                        React.createElement("i", { className: "fa-light fa-ban" }),
                        ` ${translate('table.buttons.cancel', { namespace: NS })}`)),
                    !item.isCancelled && Boolean(item.buildingId) && onEditExpiration && (React.createElement("button", { type: "button", title: translate('table.buttons.edit_expiration', { namespace: NS }), className: "qmb-ui-button", onClick: () => {
                            closePopup();
                            onEditExpiration(item);
                        } },
                        React.createElement("i", { className: "fa-light fa-calendar-pen" }),
                        ` ${translate('table.buttons.edit_expiration', { namespace: NS })}`)))))));
}
